import { useState } from 'react'
import {
  SectionHeader, Panel, ErpTable, Th, Td, EmptyRow, KpiStrip, MetricCard,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { FilterBar } from '@/shared/ui/filters'
import { Modal, Field } from '@/shared/ui/Modal'
import {
  claimFlag, flagResolutions, flagSeverities, flagStatuses, integrityCounts,
  listIntegrityFlags, resolveFlag, type IntegrityFlag,
} from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'

/**
 * Integrity — the flag queue. `turnout:moderate` authority.
 *
 * ## What this replaces
 *
 * A paragraph promising a queue "in W4", over a feature that had no read endpoint at all.
 * The flags themselves were never hypothetical: `CheckinService` writes a
 * `CHECKIN_TERMINAL_REGRESSION` row every time a confirmed VOTED state is contradicted by
 * a later report. Those rows have been accumulating in `turnout_integrity_flag` since the
 * feature shipped, in a table nothing could query.
 *
 * ## A flag is never deleted
 *
 * Resolving one writes a resolution, a reviewer and a timestamp and leaves the row. The
 * default filter is OPEN because that is the work; clearing the filter shows the audit
 * trail, which is the reason the rows are kept.
 */
export function IntegrityPage() {
  const canModerate = useCan('turnout:moderate')
  const selection = useElection()
  const { electionId } = selection
  const [status, setStatus] = useState('OPEN')
  const [severity, setSeverity] = useState('')
  const [resolving, setResolving] = useState<IntegrityFlag | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const statuses = useLoad(flagStatuses, [])
  const severities = useLoad(flagSeverities, [])
  const counts = useLoad(
    () => (electionId ? integrityCounts(electionId) : Promise.resolve<Record<string, number>>({})),
    [electionId])
  const flags = useLoad(
    () => (electionId
      ? listIntegrityFlags(electionId, {
          status: status || undefined, severity: severity || undefined, limit: 100 })
      : Promise.resolve([])),
    [electionId, status, severity])

  if (!canModerate) return <Forbidden what="Integrity flags" />

  const rows = flags.data ?? []
  const c = counts.data ?? {}

  const claim = async (flag: IntegrityFlag) => {
    setBusy(true); setRowError(null)
    try { await claimFlag(flag.id); flags.reload(); counts.reload() }
    catch (e) { setRowError(explain(e)) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Integrity"
        description="Flags raised by the rules engine. Review and resolve, or dismiss with a note — a flag is never deleted."
        right={<ElectionPicker selection={selection} />}
      />
      <ErrorStrip message={rowError ?? selection.error ?? counts.error ?? flags.error} />

      <KpiStrip>
        <MetricCard label="Open" value={(c.OPEN ?? 0).toLocaleString()}
          sub="nobody has claimed these" accent />
        <MetricCard label="Under review" value={(c.UNDER_REVIEW ?? 0).toLocaleString()}
          sub="somebody is looking" />
        <MetricCard label="Resolved" value={(c.RESOLVED ?? 0).toLocaleString()}
          sub="closed with a decision" />
        <MetricCard label="Dismissed" value={(c.DISMISSED ?? 0).toLocaleString()}
          sub="found to be a system error" />
      </KpiStrip>

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Status
          <select className="erp-input h-9" value={status}
            disabled={statuses.loading}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="">All — including the audit trail</option>
            {(statuses.data ?? []).map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Severity
          <select className="erp-input h-9" value={severity}
            disabled={severities.loading}
            onChange={(e) => setSeverity(e.target.value)}>
            <option value="">Any severity</option>
            {(severities.data ?? []).map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
        </label>
      </FilterBar>

      <Panel title="Flags" description="Newest first. Each row: what it is about, which rule fired, and what was decided.">
        {flags.loading && rows.length === 0 ? <LoadingBlock label="Loading flags…" /> : (
          <ErpTable head={(
            <>
              <Th>Severity</Th>
              <Th>Rule</Th>
              <Th>Subject</Th>
              <Th>Detected</Th>
              <Th>Status</Th>
              <Th>Resolution</Th>
              <Th />
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7} message={status
                ? `No flags with status ${label(status)}. Clear the filter to see the full trail.`
                : 'No integrity flags in this election. Nothing has contradicted itself.'} />
            ) : rows.map((f) => (
              <tr key={f.id} className="hover:bg-muted/40 align-top">
                <Td>
                  <span className={f.severityCode === 'CRITICAL'
                    ? 'text-red-700 font-medium'
                    : f.severityCode === 'WARNING' ? 'text-amber-700' : 'text-muted-foreground'}>
                    {label(f.severityCode)}
                  </span>
                </Td>
                <Td>
                  <span className="block font-mono text-erp-xs">{f.ruleCode}</span>
                  {f.detail && f.detail !== '{}' && (
                    <span className="block text-erp-xs text-muted-foreground break-all">
                      {f.detail}
                    </span>
                  )}
                </Td>
                <Td>
                  {/* Who or what the flag is about, in words. A queue reading
                      "CHECKIN 3f2a91c4" cannot tell a reviewer whether a contradicted
                      vote is a mis-tap or something worse. */}
                  <span className="block font-medium">
                    {f.subjectLabel ?? label(f.subjectTypeCode)}
                  </span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {f.subjectLabel ? `${label(f.subjectTypeCode)} · ` : ''}
                    {f.unitName ?? f.subjectId.slice(0, 8)}
                  </span>
                </Td>
                <Td className="text-muted-foreground">
                  {new Date(f.detectedAt).toLocaleString()}
                </Td>
                <Td>{label(f.statusCode)}</Td>
                <Td className="text-muted-foreground">
                  {f.resolutionCode
                    ? (
                      <>
                        <span className="block">{label(f.resolutionCode)}</span>
                        {f.resolutionNote && (
                          <span className="block text-erp-xs">{f.resolutionNote}</span>
                        )}
                      </>
                    )
                    : '—'}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {f.statusCode === 'OPEN' && (
                    <button type="button" className="erp-btn h-8 mr-1"
                      disabled={busy} onClick={() => claim(f)}>
                      Claim
                    </button>
                  )}
                  {(f.statusCode === 'OPEN' || f.statusCode === 'UNDER_REVIEW') && (
                    <button type="button" className="erp-btn erp-btn-primary h-8"
                      disabled={busy} onClick={() => { setRowError(null); setResolving(f) }}>
                      Resolve
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      {resolving && (
        <ResolveModal
          flag={resolving}
          onClose={() => setResolving(null)}
          onDone={() => { setResolving(null); flags.reload(); counts.reload() }}
        />
      )}
    </div>
  )
}

/**
 * Close a flag.
 *
 * The resolution list is served, so this cannot offer a code the server would reject.
 * `SYSTEM_ERROR` closes the flag as DISMISSED rather than RESOLVED — the anomaly was ours,
 * not the field's — and the modal says so rather than leaving it a surprise.
 */
function ResolveModal({ flag, onClose, onDone }: {
  flag: IntegrityFlag
  onClose: () => void
  onDone: () => void
}) {
  const resolutions = useLoad(flagResolutions, [])
  const [resolution, setResolution] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!resolution) return
    setBusy(true); setError(null)
    try { await resolveFlag(flag.id, resolution, note || null); onDone() }
    catch (e) { setError(explain(e)); setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Resolve this flag"
      description={`${flag.ruleCode} · ${label(flag.severityCode)}`}
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !resolution} onClick={submit}>
            {busy ? 'Saving…' : 'Resolve'}
          </button>
        </>
      )}>
      <ErrorStrip message={error} />
      <div className="space-y-3">
        <Field label="Resolution" required
          hint={resolution === 'SYSTEM_ERROR'
            ? 'Closes the flag as DISMISSED — the anomaly was in the system, not the field.'
            : 'What was decided. The flag row and its detail are kept either way.'}>
          <select className="erp-input h-9 w-full" value={resolution}
            disabled={resolutions.loading}
            onChange={(e) => setResolution(e.target.value)}>
            <option value="">Choose…</option>
            {(resolutions.data ?? []).map((r) => (
              <option key={r} value={r}>{label(r)}</option>
            ))}
          </select>
        </Field>
        <Field label="Note"
          hint="The only place the human reasoning lives. Somebody will read this months from now.">
          <textarea className="erp-input w-full min-h-20" value={note} maxLength={512}
            onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
